import React, { useState, useEffect } from 'react';
import { smsReceiverApi } from '../api/smsApi';
import ReceiverManageModal from '../components/ReceiverManageModal';
// [CONTACT_BULK_V1] 폰 연락처 여러 명 일괄 등록
import ContactBulkImportModal from '../components/ContactBulkImportModal';
// [GROUP_MANAGE_V1] 그룹명 일괄 변경
import GroupManageModal from '../components/GroupManageModal';
// [GROUP_FILTER_V1] 그룹 목록을 서버(sms_groups)에서 받기 위한 API.
// 수신처에서 역산하면 '사람 0명인 그룹'이 안 보여 첫 사람을 넣을 수가 없다.
import { smsGroupApi } from '../api/smsApi';
// [PHONE_FMT_V1] 화면 표시용 하이픈 포맷
import { canPickContacts, normalizePhone } from '../utils/contactPicker';
import './ReceiverManagePage.css';

// ─────────────────────────────────────────────────────────────
// SMS 수신처 관리 페이지
//
// [LAYOUT_V6] <table> 을 쓰지 않고 div + CSS grid 로 목록을 그린다.
//   이전에는 <table> 안에 <tr><td colspan="6"> 으로 상세를 펼쳤는데,
//   표의 폭 계산 규칙 때문에 상세 영역이 좁게 눌리는 문제가 반복됐다.
//   div 구조에서는 그런 제약이 없어 상세가 항상 전체 폭을 쓴다.
//
// 동작 흐름:
//   1) 목록 행 클릭  → 그 아래로 상세 영역이 펼쳐짐 (다시 클릭하면 닫힘)
//   2) [수정] 클릭   → 입력칸으로 전환
//   3) [수정완료]    → 서버 저장(PUT) 후 목록 새로고침
//   4) [삭제]        → 확인 후 삭제(DELETE)
//
// 연관 파일:
//   ../api/smsApi.js                     서버 통신
//   ../components/ReceiverManageModal    새 수신처 등록
//   ../components/ContactBulkImportModal 폰 연락처 일괄 등록
//   ../components/GroupManageModal       그룹명 일괄 변경
//   ./ReceiverManagePage.css             이 화면 전용 스타일
// ─────────────────────────────────────────────────────────────

// 등록일 표시용 — "2026-08-12T07:30:00.000Z" → "2026-08-12"
function fmtDate(v) {
  if (!v) return "-";
  const s = String(v);
  // 앞 10글자가 이미 YYYY-MM-DD 형태면 그대로 (시간대 변환으로 날짜가 밀리는 것 방지)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d) ? "-" : d.toISOString().slice(0, 10);
}

export default function ReceiverManagePage() {
  const [receivers, setReceivers]   = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen]   = useState(false);   // [CONTACT_BULK_V1]
  const [isGroupOpen, setIsGroupOpen] = useState(false);   // [GROUP_MANAGE_V1]

  const [openId, setOpenId] = useState(null);   // 펼쳐진 행의 id
  const [editId, setEditId] = useState(null);   // 수정모드인 행의 id
  const [form,   setForm]   = useState({});     // 수정 중인 입력값
  const [saving, setSaving] = useState(false);
  // [GROUP_SELECT_V1] 그룹 칸을 '직접 입력' 모드로 전환했는지
  const [newGroupMode, setNewGroupMode] = useState(false);

  // [GROUP_FILTER_V1]
  //   serverGroups : sms_groups 테이블의 그룹 (0명 그룹 포함)
  //   filterGroup  : 화면에서 고른 그룹. ""(빈 문자열) 이면 전체
  const [serverGroups, setServerGroups] = useState([]);
  const [filterGroup,  setFilterGroup]  = useState("");
  // [CAT_FILTER_V1] 구분(직원·거래처·고객) 필터. "" 이면 전체
  const [filterCat, setFilterCat] = useState("");

  const fetchList = async () => {
    try {
      const res = await smsReceiverApi.getList();
      if (res.ok) setReceivers(res.items);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchList(); fetchGroups(); }, []);

  // [GROUP_FILTER_V1] 서버에서 그룹 목록을 받는다.
  // fetchList 와 따로 도는 이유: 그룹은 수신처가 없어도 존재할 수 있기 때문.
  const fetchGroups = async () => {
    try {
      const res = await smsGroupApi.list();
      if (res.ok) setServerGroups(res.items || []);
    } catch (err) { console.error(err); }
  };

  // [GROUP_FILTER_V1] 드롭다운·버튼에 쓸 그룹명 목록.
  //   서버 그룹 + 수신처에만 있는 그룹을 합친다.
  //   (동기화가 어긋나도 기존 데이터가 화면에서 사라지지 않게 하는 안전장치)
  const groupList = [...new Set([
    ...serverGroups.map(g => g.name),
    ...receivers.map(r => r.group_name).filter(Boolean),
  ])].sort();

  // [GROUP_FILTER_V1] 그룹별 인원수 — 버튼에 숫자로 표시
  const countOf = (g) => receivers.filter(r => (r.group_name || "") === g).length;

  // [CAT_FILTER_V1] 명부에 실제로 쓰인 구분값만 뽑는다.
  //   구분은 자유 입력칸이라 고정 목록을 두면 새 값이 안 보인다.
  const catList = [...new Set(receivers.map(r => r.category).filter(Boolean))].sort();

  // [CAT_FILTER_V1] 구분별 인원수 — 드롭다운에 숫자로 표시
  const catCountOf = (c) => receivers.filter(r => (r.category || "") === c).length;

  // [GROUP_FILTER_V1 + CAT_FILTER_V1]
  //   그룹과 구분을 모두 만족하는 사람만 남긴다(AND).
  //   각 필터는 "" 면 그 조건을 통과시킨다(= 전체).
  const shownReceivers = receivers.filter(r =>
    (!filterGroup || (r.group_name || "") === filterGroup) &&
    (!filterCat   || (r.category   || "") === filterCat)
  );

  const handleRowClick = (id) => {
    if (openId === id) { setOpenId(null); setEditId(null); }
    else               { setOpenId(id);   setEditId(null); }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setNewGroupMode(false);   // 수정 시작 시 항상 드롭다운부터
    setForm({
      name       : r.name       || "",
      group_name : r.group_name || "",
      phone      : r.phone      || "",
      category   : r.category   || "",
      memo       : r.memo       || "",
    });
  };

  const saveEdit = async (id) => {
    if (!form.name?.trim())  { alert("성함을 입력해 주세요.");   return; }
    if (!form.phone?.trim()) { alert("연락처를 입력해 주세요."); return; }
    setSaving(true);
    try {
      const res = await smsReceiverApi.update(id, form);
      if (res.ok) { setEditId(null); await fetchList(); }
      else        { alert("저장 실패: " + (res.error || "알 수 없는 오류")); }
    } catch (err) { alert("수정 중 오류가 발생했습니다."); }
    finally       { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      const res = await smsReceiverApi.delete(id);
      if (res.ok) { setOpenId(null); setEditId(null); fetchList(); }
    } catch (err) { alert("삭제 중 오류가 발생했습니다."); }
  };

  return (
    <div className="rm-page">
      {/* ── 머리말 + 버튼 ─────────────────────────────── */}
      <div className="rm-header">
        <div>
          <h2>📱 SMS 수신처 관리</h2>
          <p className="rm-sub">목록을 클릭하면 상세 내용과 수정·삭제 버튼이 열립니다.</p>
        </div>
        <div className="rm-actions">
          <button className="rm-btn rm-btn-primary" onClick={() => setIsModalOpen(true)}>
            + 새 수신처 등록
          </button>
          {/* [CONTACT_BULK_V1] 안드로이드 Chrome(HTTPS)에서만 보인다 */}
          {canPickContacts() && (
            <button className="rm-btn rm-btn-blue" onClick={() => setIsBulkOpen(true)}>
              📱 연락처 일괄 등록
            </button>
          )}
          {/* [GROUP_MANAGE_V1] 그룹명을 한 번에 바꾸는 화면 */}
          <button className="rm-btn rm-btn-gray" onClick={() => setIsGroupOpen(true)}>
            🏷️ 그룹 관리
          </button>
        </div>
      </div>

      {/* [GROUP_FILTER_V2] 그룹 골라 보기 — 드롭다운 한 줄.
          V1 은 그룹마다 버튼을 그려 11개만 되어도 두세 줄을 먹었다.
          드롭다운은 그룹이 늘어도 높이가 그대로다.
          0명 그룹은 "(0)" 로 표시해 만들어만 둔 그룹임을 알 수 있게 한다. */}
      <div className="rm-gfilter">
        <select
          className="rm-gselect"
          value={filterGroup}
          onChange={e => setFilterGroup(e.target.value)}>
          <option value="">전체 ({receivers.length}명)</option>
          {groupList.map(g => (
            <option key={g} value={g}>{g} ({countOf(g)})</option>
          ))}
        </select>
        {/* [CAT_FILTER_V1] 구분 드롭다운. 그룹과 AND 로 걸린다 */}
        <select
          className="rm-gselect rm-gselect-cat"
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}>
          <option value="">구분 전체</option>
          {catList.map(c => (
            <option key={c} value={c}>{c} ({catCountOf(c)})</option>
          ))}
        </select>

        {/* 둘 중 하나라도 걸려 있으면 해제 버튼 — 한 번에 둘 다 푼다 */}
        {(filterGroup || filterCat) && (
          <button className="rm-gclear"
            onClick={() => { setFilterGroup(""); setFilterCat(""); }}>
            ✕ 전체 보기
          </button>
        )}
        <span className="rm-gcount">{shownReceivers.length}명</span>
      </div>

      {/* ── 목록 ──────────────────────────────────────── */}
      <div className="rm-list">
        {/* 열 머리글 (좁은 화면에서는 CSS 로 숨김) */}
        <div className="rm-head">
          <span>그룹</span><span>성함</span><span>연락처</span>
          <span>구분</span><span>메모</span><span>등록일</span>
        </div>

        {shownReceivers.length === 0 && (
          <div className="rm-empty">
            {(filterGroup || filterCat)
              ? `${filterGroup ? `"${filterGroup}" 그룹` : ""}` +
                `${filterGroup && filterCat ? " · " : ""}` +
                `${filterCat ? `구분 "${filterCat}"` : ""}` +
                ` 에 해당하는 수신처가 없습니다.`
              : "등록된 수신처가 없습니다."}
          </div>
        )}

        {shownReceivers.map(r => (
          <div className="rm-item" key={r.id}>
            {/* 요약행 */}
            <div
              className={`rm-row ${openId === r.id ? "is-open" : ""}`}
              onClick={() => handleRowClick(r.id)}
            >
              <span className="rm-c-group"><em className="rm-badge">{r.group_name}</em></span>
              <span className="rm-c-name">
                <i className="rm-arrow">{openId === r.id ? "▼" : "▶"}</i>{r.name}
              </span>
              <span className="rm-c-phone">{normalizePhone(r.phone)}</span>
              <span className="rm-c-cat">
                <em className={`rm-chip ${r.category === '거래처' ? 'is-partner' : ''}`}>{r.category}</em>
              </span>
              <span className="rm-c-memo" title={r.memo}>{r.memo || "-"}</span>
              <span className="rm-c-date">{fmtDate(r.created_at)}</span>
            </div>

            {/* 상세 (표가 아니므로 항상 전체 폭을 쓴다) */}
            {openId === r.id && (
              <div className="rm-detail">
                {editId === r.id ? (
                  /* ===== 수정 모드 ===== */
                  <>
                    <div className="rm-field">
                      <label>그룹</label>
                      {/* [GROUP_SELECT_V1] 기존 그룹은 드롭다운, 새 그룹은 직접 입력.
                          오타로 비슷한 그룹이 늘어나는 것을 막는다. */}
                      {newGroupMode ? (
                        <span className="rm-val rm-inline">
                          <input
                            className="rm-input" value={form.group_name} autoFocus
                            placeholder="새 그룹명 입력"
                            onChange={e => setForm({ ...form, group_name: e.target.value })}
                          />
                          <button className="rm-mini" type="button"
                            onClick={() => { setNewGroupMode(false); setForm({ ...form, group_name: "" }); }}>
                            목록
                          </button>
                        </span>
                      ) : (
                        <span className="rm-val">
                          <select
                            className="rm-input"
                            value={groupList.includes(form.group_name) ? form.group_name : ""}
                            onChange={e => {
                              if (e.target.value === "__new__") {
                                setNewGroupMode(true);
                                setForm({ ...form, group_name: "" });
                              } else {
                                setForm({ ...form, group_name: e.target.value });
                              }
                            }}>
                            <option value="">-- 그룹 선택 --</option>
                            {groupList.map((g, i) => <option key={i} value={g}>{g}</option>)}
                            <option value="__new__">✏️ 새 그룹 직접 입력...</option>
                          </select>
                        </span>
                      )}
                    </div>

                    <div className="rm-field">
                      <label>성함</label>
                      <span className="rm-val">
                        <input className="rm-input" value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })} />
                      </span>
                    </div>
                    <div className="rm-field">
                      <label>연락처</label>
                      <span className="rm-val">
                        {/* [PHONE_FMT_V1] 입력하는 동안 010-1234-5678 형태로 자동 정리.
                            서버가 저장할 때 하이픈을 지우므로 DB 값은 지금과 같다. */}
                        <input className="rm-input" value={form.phone} inputMode="numeric"
                          onChange={e => setForm({ ...form, phone: normalizePhone(e.target.value) })} />
                      </span>
                    </div>
                    <div className="rm-field">
                      <label>구분</label>
                      <span className="rm-val">
                        <input className="rm-input" value={form.category}
                          onChange={e => setForm({ ...form, category: e.target.value })} />
                      </span>
                    </div>
                    <div className="rm-field">
                      <label>메모</label>
                      <span className="rm-val">
                        <textarea className="rm-input rm-textarea" value={form.memo}
                          onChange={e => setForm({ ...form, memo: e.target.value })} />
                      </span>
                    </div>

                    <div className="rm-detail-btns">
                      <button className="rm-btn rm-btn-green" onClick={() => saveEdit(r.id)} disabled={saving}>
                        {saving ? "저장 중..." : "💾 수정완료"}
                      </button>
                      <button className="rm-btn rm-btn-plain" onClick={() => setEditId(null)}>
                        취소
                      </button>
                    </div>
                  </>
                ) : (
                  /* ===== 보기 모드 ===== */
                  <>
                    <div className="rm-field"><label>그룹</label><span className="rm-val">{r.group_name || "-"}</span></div>
                    <div className="rm-field"><label>성함</label><span className="rm-val">{r.name}</span></div>
                    <div className="rm-field"><label>연락처</label><span className="rm-val">{normalizePhone(r.phone)}</span></div>
                    <div className="rm-field"><label>구분</label><span className="rm-val">{r.category || "-"}</span></div>
                    <div className="rm-field"><label>메모</label><span className="rm-val rm-pre">{r.memo || "-"}</span></div>
                    <div className="rm-field"><label>등록일</label><span className="rm-val">{fmtDate(r.created_at)}</span></div>

                    <div className="rm-detail-btns">
                      <button className="rm-btn rm-btn-primary" onClick={() => startEdit(r)}>✏️ 수정</button>
                      <button className="rm-btn rm-btn-danger" onClick={() => handleDelete(r.id)}>🗑️ 삭제</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 새 수신처 등록 — editItem 을 항상 null 로 넘겨 신규모드 고정 */}
      <ReceiverManageModal
        open={isModalOpen}
        editItem={null}
        onClose={() => setIsModalOpen(false)}
        onRefresh={fetchList}
        groups={groupList}
      />

      {/* [CONTACT_BULK_V1] existing 으로 현재 목록을 넘겨 중복 번호를 판별한다 */}
      <ContactBulkImportModal
        open={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        onRefresh={fetchList}
        existing={receivers}
        groups={groupList}
      />

      {/* [GROUP_MANAGE_V1] 그룹명 일괄 변경 */}
      <GroupManageModal
        open={isGroupOpen}
        onClose={() => setIsGroupOpen(false)}
        onRefresh={() => { fetchList(); fetchGroups(); }}
        receivers={receivers}
      />
    </div>
  );
}
